// Every way an import can be refused, as one actionable panel (Module 02 §3.3,
// §9.3, §9.15, §7).
//
// The doctrine line this component holds: a factory admin never sees a raw code.
// `HAS_ERRORS`, `MAPPING_INCOMPLETE`, `IMPORT_KIND_NOT_AVAILABLE`,
// `FILE_TOO_LARGE`, `BAD_ENCODING`, `FINANCE_REQUIRED_FOR_VALUED_STOCK`,
// `IMPORT_STATE_INVALID` and the plain 403/404 each get copy that says what
// happened and what to do next; anything unclassified falls back to the shared
// §4 error card (title + detail + request id + retry) rather than being guessed
// at.

import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { ErrorState } from "../../../../components/states";
import {
  MAX_FILE_MB,
  MAX_ROWS,
  classifyImportProblem,
  type ImportKind,
  type ImportProblem,
} from "./import-model";
import { useImportLabels } from "./labels";

export interface ImportProblemNoticeProps {
  /** The thrown failure; `null`/`undefined` renders nothing. */
  error: unknown;
  /** Retry the action that failed (also the generic card's button). */
  onRetry?: (() => void) | undefined;
  /** The job's lane, when known, for the refusals that read per lane. */
  kind?: ImportKind | undefined;
}

export function ImportProblemNotice({ error, onRetry, kind }: ImportProblemNoticeProps) {
  const { t } = useTranslation();
  const labels = useImportLabels();
  const problem = classifyImportProblem(error);
  if (problem === undefined) return null;

  if (problem.kind === "generic") {
    return (
      <ErrorState
        title={t("onboarding.importShared.problems.genericTitle")}
        detail={t("onboarding.importShared.problems.genericBody")}
        retryLabel={t("onboarding.state.retry")}
        onRetry={onRetry ?? (() => undefined)}
        requestId={problem.requestId}
        requestIdLabel={t("onboarding.state.requestId")}
      />
    );
  }

  const { title, body } = copyFor(problem, t, labels.terms, kind);

  return (
    <div
      className="state state-error wizard-notice import-problem"
      role="alert"
      data-testid="import-problem"
      data-problem={problem.kind}
    >
      <p className="state-title">{title}</p>
      <p className="state-detail">{body}</p>
      {problem.fields.length > 0 ? (
        <ul className="wizard-notice-list">
          {problem.fields.map((field) => (
            <li key={field}>{labels.fieldLabel(field)}</li>
          ))}
        </ul>
      ) : null}
      {problem.requestId !== undefined ? (
        <p className="state-request-id">
          {t("onboarding.state.requestId")}: <code>{problem.requestId}</code>
        </p>
      ) : null}
      {onRetry !== undefined ? (
        <Button onClick={onRetry}>{t("onboarding.state.retry")}</Button>
      ) : null}
    </div>
  );
}

type Translate = ReturnType<typeof useTranslation>["t"];

function copyFor(
  problem: ImportProblem,
  t: Translate,
  terms: Record<string, string>,
  kind: ImportKind | undefined,
): { title: string; body: string } {
  const P = "onboarding.importShared.problems";
  switch (problem.kind) {
    case "has_errors":
      return { title: t(`${P}.hasErrorsTitle`), body: t(`${P}.hasErrorsBody`) };
    case "mapping_incomplete":
      return { title: t(`${P}.mappingIncompleteTitle`), body: t(`${P}.mappingIncompleteBody`) };
    case "kind_not_available":
      return {
        title: t(`${P}.kindNotAvailableTitle`),
        body: t(`${P}.kindNotAvailableBody`, {
          module: problem.ownerModule ?? t(`${P}.kindNotAvailableFallback`),
        }),
      };
    case "file_too_large":
      return {
        title: t(`${P}.fileTooLargeTitle`),
        body: t(`${P}.fileTooLargeBody`, {
          sizeMb: MAX_FILE_MB,
          maxRows: MAX_ROWS.toLocaleString(),
        }),
      };
    case "bad_encoding":
      return { title: t(`${P}.badEncodingTitle`), body: t(`${P}.badEncodingBody`) };
    case "empty_file":
      return { title: t(`${P}.emptyFileTitle`), body: t(`${P}.emptyFileBody`) };
    case "merged_cells":
      return { title: t(`${P}.mergedCellsTitle`), body: t(`${P}.mergedCellsBody`) };
    case "finance_required":
      return { title: t(`${P}.financeRequiredTitle`), body: t(`${P}.financeRequiredBody`) };
    case "state_invalid":
      return { title: t(`${P}.stateInvalidTitle`), body: t(`${P}.stateInvalidBody`) };
    case "not_found":
      return { title: t(`${P}.notFoundTitle`), body: t(`${P}.notFoundBody`) };
    case "facility_required":
      return {
        title: t(`${P}.facilityRequiredTitle`, { facility: terms["facility"] ?? "" }),
        body: t(`${P}.facilityRequiredBody`, { facility: terms["facility"] ?? "" }),
      };
    case "forbidden":
      return { title: t(`${P}.forbiddenTitle`), body: t(`${P}.forbiddenBody`) };
    // 422 IMPORT_IMPORTER_NOT_PERMITTED (spec 09 §3.15 and §3.15b, D-325
    // ruling 2): about the job's CREATOR, whoever pressed. Never the reader's
    // own 403, which would tell them to ask for a role they hold.
    case "importer_not_permitted":
      return {
        title: t(`${P}.importerNotPermittedTitle`),
        body:
          kind === "open_sales_orders"
            ? t(`${P}.importerNotPermittedOrdersBody`)
            : t(`${P}.importerNotPermittedBody`),
      };
    case "generic":
      return { title: t(`${P}.genericTitle`), body: t(`${P}.genericBody`) };
  }
}
